import {
  BadgePill,
  Breakpoints,
  StandardTable,
  StandardTableColumn,
  StandardTableRow,
} from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import { useFetch } from '@api/ApiHelper';
import { useCustomRole } from '@api/v4/organizations/custom_permissions';
import { BFLoader } from '@components/common/loader/main';
import { BrandfolderHexColors } from '@components/library/utils';
import { LimitsTableRowInput } from '@components/limits/LimitsTableRowInput';

import { AllocationAlert } from './AllocationAlert';
import {
  CreateLimitData,
  FlattenedApiData,
  LimitsApiDataResponse,
  LimitTypes,
  PlanLimitsApiDataResponse,
  UpdateLimitData,
} from './customLimitsTypes';
import {
  flattenLimitsData,
  organizeLimitsFolderData,
  organizeNoLimitsFolderData,
} from './helpers/flattenLimitsData';
import { postLimits } from './helpers/postLimits';
import { ResourceLabel } from './ResourceLabel';
import './styles/folder-limits-table.scss';
import { SubmitLimitsButton } from './SubmitLimitsButton';

type RoleType = 'Admin' | 'Approver' | 'Collaborator' | 'Custom';

const ErrorPlaceholderDiv = styled.div`
  height: 41px;
  font-size: 13px;
  visibility: hidden;
`;

const LimitColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;

  .bf-textfield__header {
    margin-block-end: 0;
  }
`;

const TruncatedBadgePill = styled(BadgePill)`
  max-width: 120px;

  &.bf-pill {
    max-width: 140px;
  }

  .bf-pill__content {
    max-width: 100%;
  }

  .truncate-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

interface ResourceGroup {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  roles: CombinedRoleFolderData[];
}

interface CombinedRoleFolderData extends FlattenedApiData {
  role: RoleType;
  customRoleName?: string;
}

interface CombinedRoleLimitsTableProps {
  orgKey: string;
  setOpen: (isOpen: boolean) => void;
}

export const CombinedRoleLimitsTable: FunctionComponent<CombinedRoleLimitsTableProps> = ({
  orgKey,
  setOpen,
}) => {
  // Fetch custom role information
  const { customRole, loading: customRoleLoading } = useCustomRole(orgKey);
  const brandfoldersGetUrl = `/api/v4/organizations/${orgKey}/brandfolders?fields=current_usage`;
  const brandguidesGetUrl = `/api/v4/organizations/${orgKey}/brandguides?fields=current_usage`;
  const organizationGetUrl = `/api/v4/organizations/${orgKey}?fields=current_usage`;
  const portalsGetUrl = `/api/v4/private/organizations/${orgKey}/portals?fields=current_usage`;

  // Fetch admin limits
  const bfAdminLimitsGetUrl = `/api/v4/organizations/${orgKey}/limits/brandfolder/admin?include=resource`;
  const brandguideAdminLimitsUrl = `/api/v4/organizations/${orgKey}/limits/brandguide/admin?include=resource`;
  const orgAdminLimitsGetUrl = `/api/v4/organizations/${orgKey}/limits/organization/admin?include=resource`;
  const portalsAdminLimitsUrl = `/api/v4/organizations/${orgKey}/limits/portal/admin?include=resource`;

  // Fetch collaborator limits
  const bfCollabLimitsGetUrl = `/api/v4/organizations/${orgKey}/limits/brandfolder/collaborator?include=resource`;
  const brandguideCollabLimitsUrl = `/api/v4/organizations/${orgKey}/limits/brandguide/collaborator?include=resource`;
  const orgCollabLimitsGetUrl = `/api/v4/organizations/${orgKey}/limits/organization/collaborator?include=resource`;
  const portalsCollabLimitsUrl = `/api/v4/organizations/${orgKey}/limits/portal/collaborator?include=resource`;

  // Fetch custom collaborator limits (if custom role exists)
  const customPermissionKey = customRole?.key;
  const bfCustomLimitsGetUrl = customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/brandfolder/collaborator/${customPermissionKey}?include=resource` : null;
  const brandguideCustomLimitsUrl = customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/brandguide/collaborator/${customPermissionKey}?include=resource` : null;
  const orgCustomLimitsGetUrl = customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/organization/collaborator/${customPermissionKey}?include=resource` : null;
  const portalsCustomLimitsUrl = customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/portal/collaborator/${customPermissionKey}?include=resource` : null;

  const planLimitGetUrl = `/api/v4/organizations/${orgKey}/plan`;

  const adminLimitsPatchUrl = `/api/v4/organizations/${orgKey}/limits/organization/admin`;
  const collabLimitsPatchUrl = `/api/v4/organizations/${orgKey}/limits/organization/collaborator`;
  const customLimitsPatchUrl = customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/organization/collaborator/${customPermissionKey}` : null;

  const foldersWithLimitsPayload = [];

  const brandfoldersFetch = useFetch<LimitsApiDataResponse>({
    url: brandfoldersGetUrl,
    fetchOnMount: true,
  });

  const brandguidesFetch = useFetch<LimitsApiDataResponse>({
    url: brandguidesGetUrl,
    fetchOnMount: true,
  });

  const organizationFetch = useFetch<LimitsApiDataResponse>({
    url: organizationGetUrl,
    fetchOnMount: true,
  });

  const portalsFetch = useFetch<LimitsApiDataResponse>({
    url: portalsGetUrl,
    fetchOnMount: true,
  });

  const bfAdminLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: bfAdminLimitsGetUrl,
    fetchOnMount: true,
  });

  const brandguideAdminLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: brandguideAdminLimitsUrl,
    fetchOnMount: true,
  });

  const orgAdminLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: orgAdminLimitsGetUrl,
    fetchOnMount: true,
  });

  const portalsAdminLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: portalsAdminLimitsUrl,
    fetchOnMount: true,
  });

  const bfCollabLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: bfCollabLimitsGetUrl,
    fetchOnMount: true,
  });

  const brandguideCollabLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: brandguideCollabLimitsUrl,
    fetchOnMount: true,
  });

  const orgCollabLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: orgCollabLimitsGetUrl,
    fetchOnMount: true,
  });

  const portalsCollabLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: portalsCollabLimitsUrl,
    fetchOnMount: true,
  });

  const bfCustomLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: bfCustomLimitsGetUrl || '',
    fetchOnMount: !!bfCustomLimitsGetUrl,
  });

  const brandguideCustomLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: brandguideCustomLimitsUrl || '',
    fetchOnMount: !!brandguideCustomLimitsUrl,
  });

  const orgCustomLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: orgCustomLimitsGetUrl || '',
    fetchOnMount: !!orgCustomLimitsGetUrl,
  });

  const portalsCustomLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: portalsCustomLimitsUrl || '',
    fetchOnMount: !!portalsCustomLimitsUrl,
  });

  const planLimitFetch = useFetch<PlanLimitsApiDataResponse>({
    url: planLimitGetUrl,
    fetchOnMount: true,
  });

  const adminLimitsPatchFetch = useFetch<UpdateLimitData>({
    url: adminLimitsPatchUrl,
    method: 'PATCH',
    fetchOnMount: false,
    body: {
      data: foldersWithLimitsPayload,
    },
  });

  const collabLimitsPatchFetch = useFetch<UpdateLimitData>({
    url: collabLimitsPatchUrl,
    method: 'PATCH',
    fetchOnMount: false,
    body: {
      data: foldersWithLimitsPayload,
    },
  });

  const customLimitsPatchFetch = useFetch<UpdateLimitData>({
    url: customLimitsPatchUrl || '',
    method: 'PATCH',
    fetchOnMount: false,
    body: {
      data: foldersWithLimitsPayload,
    },
  });

  const handleSuccess = useCallback((): void => {
    // eslint-disable-next-line no-undef
    Notify.create({
      title: t`Successfully updated your limits`,
      type: 'success',
    });
    setOpen(false);
  }, [setOpen]);

  const handleError = (): void => {
    // eslint-disable-next-line no-undef
    Notify.create({
      type: 'error',
      title: t`Error updating limits`,
    });
  };

  const [limitsSuccess, setLimitsSuccess] = useState<boolean>();
  const [folders, setFolders] = useState<CombinedRoleFolderData[]>([]);
  const [resourceGroups, setResourceGroups] = useState<ResourceGroup[]>([]);
  const [submitLimitsDisabled, setSubmitLimitsDisabled] = useState(true);

  // Track error states from child components for alignment
  const [errorStates, setErrorStates] = useState<Map<string, boolean>>(new Map());

  const sumFoldersLimit = folders.reduce(
    (sum, { currentLimit }) => sum + currentLimit || 0,
    0
  );

  const sumFoldersUsage = folders.reduce(
    (sum, { currentUsage }) => sum + (currentUsage || 0), 0
  ) || 0;

  useEffect(() => {
    if (
      brandfoldersFetch?.response &&
      brandguidesFetch?.response &&
      organizationFetch?.response &&
      portalsFetch?.response &&
      bfAdminLimitsFetch?.response &&
      brandguideAdminLimitsFetch?.response &&
      orgAdminLimitsFetch?.response &&
      portalsAdminLimitsFetch?.response &&
      bfCollabLimitsFetch?.response &&
      brandguideCollabLimitsFetch?.response &&
      orgCollabLimitsFetch?.response &&
      portalsCollabLimitsFetch?.response &&
      (!customPermissionKey || (
        bfCustomLimitsFetch?.response &&
        brandguideCustomLimitsFetch?.response &&
        orgCustomLimitsFetch?.response &&
        portalsCustomLimitsFetch?.response
      ))
    ) {
      const adminLimitsData = flattenLimitsData(
        organizeLimitsFolderData(
          orgAdminLimitsFetch.response,
          bfAdminLimitsFetch.response,
          portalsAdminLimitsFetch.response,
          brandguideAdminLimitsFetch.response
        ),
        organizeNoLimitsFolderData(
          !orgAdminLimitsFetch.response?.data?.length,
          brandfoldersFetch.response,
          organizationFetch.response,
          portalsFetch.response,
          brandguidesFetch.response
        ),
        'admin'
      );

      const collabLimitsData = flattenLimitsData(
        organizeLimitsFolderData(
          orgCollabLimitsFetch.response,
          bfCollabLimitsFetch.response,
          portalsCollabLimitsFetch.response,
          brandguideCollabLimitsFetch.response
        ),
        organizeNoLimitsFolderData(
          !orgCollabLimitsFetch.response?.data?.length,
          brandfoldersFetch.response,
          organizationFetch.response,
          portalsFetch.response,
          brandguidesFetch.response
        ),
        'collaborator'
      );

      // Process custom limits data if custom role exists
      let customLimitsData: any[] = [];
      if (customPermissionKey && orgCustomLimitsFetch.response) {
        customLimitsData = flattenLimitsData(
          organizeLimitsFolderData(
            orgCustomLimitsFetch.response,
            bfCustomLimitsFetch.response,
            portalsCustomLimitsFetch.response,
            brandguideCustomLimitsFetch.response
          ),
          organizeNoLimitsFolderData(
            !orgCustomLimitsFetch.response?.data?.length,
            brandfoldersFetch.response,
            organizationFetch.response,
            portalsFetch.response,
            brandguidesFetch.response
          ),
          'custom'
        );
      }

      const combinedFolders: CombinedRoleFolderData[] = [];

      adminLimitsData.forEach((adminFolder) => {
        combinedFolders.push({
          ...adminFolder,
          role: 'Admin' as RoleType,
        });
      });

      collabLimitsData.forEach((collabFolder) => {
        combinedFolders.push({
          ...collabFolder,
          role: 'Collaborator' as RoleType,
        });
      });

      // Add custom role data if available
      if (customPermissionKey && customLimitsData.length > 0) {
        customLimitsData.forEach((customFolder) => {
          combinedFolders.push({
            ...customFolder,
            role: 'Custom' as RoleType,
            customRoleName: customRole?.label,
          });
        });
      }

      adminLimitsData.forEach((adminFolder) => {
        combinedFolders.push({
          ...adminFolder,
          role: 'Approver' as RoleType,
          currentLimit: 0,
          startingLimit: 0,
          currentUsage: 0,
          hasLimits: false,
          limitId: '',
        });
      });

      setFolders(combinedFolders);

      const groupMap = new Map<string, ResourceGroup>();

      combinedFolders.forEach((folder) => {
        if (!groupMap.has(folder.resourceId)) {
          groupMap.set(folder.resourceId, {
            resourceId: folder.resourceId,
            resourceName: folder.resourceName,
            resourceType: folder.resourceType,
            roles: [],
          });
        }
        groupMap.get(folder.resourceId)!.roles.push(folder);
      });

      setResourceGroups(Array.from(groupMap.values()));
    }
  }, [
    brandfoldersFetch.response,
    brandguidesFetch.response,
    organizationFetch.response,
    portalsFetch.response,
    bfAdminLimitsFetch.response,
    brandguideAdminLimitsFetch.response,
    orgAdminLimitsFetch.response,
    portalsAdminLimitsFetch.response,
    bfCollabLimitsFetch.response,
    brandguideCollabLimitsFetch.response,
    orgCollabLimitsFetch.response,
    portalsCollabLimitsFetch.response,
    bfCustomLimitsFetch.response,
    brandguideCustomLimitsFetch.response,
    orgCustomLimitsFetch.response,
    portalsCustomLimitsFetch.response,
    customPermissionKey,
    customRole?.label,
  ]);

  useEffect(() => {
    if (adminLimitsPatchFetch.error || collabLimitsPatchFetch.error || customLimitsPatchFetch.error) {
      setLimitsSuccess(false);
    }
  }, [adminLimitsPatchFetch.error, collabLimitsPatchFetch.error, customLimitsPatchFetch.error]);

  useEffect(() => {
    if (adminLimitsPatchFetch.response || collabLimitsPatchFetch.response || customLimitsPatchFetch.response) {
      setLimitsSuccess(true);
    }
  }, [adminLimitsPatchFetch.response, collabLimitsPatchFetch.response, customLimitsPatchFetch.response]);

  useEffect(() => {
    if (limitsSuccess) {
      handleSuccess();
    } else if (limitsSuccess === false) handleError();
  }, [limitsSuccess, handleSuccess]);

  const maxOrgLimit = planLimitFetch.response?.data?.attributes.limits.combined_roles || 0;
  const maxPlanLimit: number | string = 0 <= maxOrgLimit ? maxOrgLimit : t`Unlimited`;

  const updateFolderCurrentLimit = (
    value: number,
    resourceId: string,
    role: RoleType
  ): void => {
    setFolders((prev) => {
      const updatedFolders = prev.map((folder) => {
        if (folder.resourceId === resourceId && folder.role === role) {
          return { ...folder, currentLimit: value };
        }
        return folder;
      });

      const hasChanges = updatedFolders.some(
        (folder) => folder.currentLimit !== folder.startingLimit
      );
      setSubmitLimitsDisabled(!hasChanges);

      return updatedFolders;
    });
  };

  // Handle error state changes from child components
  const handleErrorChange = useCallback((hasError: boolean, resourceId: string, limitType: LimitTypes): void => {
    const key = `${resourceId}-${limitType}`;
    setErrorStates((prev) => {
      const newMap = new Map(prev);
      if (hasError) {
        newMap.set(key, true);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  }, []);

  const submitLimitsData = (): void => {
    const adminFoldersWithLimits: CombinedRoleFolderData[] = [];
    const adminFoldersWithoutLimits: CombinedRoleFolderData[] = [];
    const customFoldersWithLimits: CombinedRoleFolderData[] = [];
    const customFoldersWithoutLimits: CombinedRoleFolderData[] = [];
    const collabFoldersWithLimits: CombinedRoleFolderData[] = [];
    const collabFoldersWithoutLimits: CombinedRoleFolderData[] = [];

    folders.forEach((folder) => {
      if (folder.role === 'Admin') {
        if (folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          adminFoldersWithLimits.push(folder);
        } else if (!folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          adminFoldersWithoutLimits.push(folder);
        }
      } else if (folder.role === 'Custom') {
        if (folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          customFoldersWithLimits.push(folder);
        } else if (!folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          customFoldersWithoutLimits.push(folder);
        }
      } else if (folder.role === 'Collaborator') {
        if (folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          collabFoldersWithLimits.push(folder);
        } else if (!folder.hasLimits && folder.currentLimit !== folder.startingLimit) {
          collabFoldersWithoutLimits.push(folder);
        }
      }
    });

    const adminLimitsPayload = [];
    if (adminFoldersWithLimits.length) {
      adminFoldersWithLimits.forEach((folder) => {
        const hasLimitObj: UpdateLimitData = {
          limit_id: folder.limitId,
          value: folder.currentLimit,
          resource_type: folder.resourceType,
        };
        adminLimitsPayload.push(hasLimitObj);
      });

      adminLimitsPatchFetch.fetch({
        url: adminLimitsPatchUrl,
        method: 'PATCH',
        body: {
          data: adminLimitsPayload,
        },
      });
    }

    const collabLimitsPayload = [];
    if (collabFoldersWithLimits.length) {
      collabFoldersWithLimits.forEach((folder) => {
        const hasLimitObj: UpdateLimitData = {
          limit_id: folder.limitId,
          value: folder.currentLimit,
          resource_type: folder.resourceType,
        };
        collabLimitsPayload.push(hasLimitObj);
      });

      collabLimitsPatchFetch.fetch({
        url: collabLimitsPatchUrl,
        method: 'PATCH',
        body: {
          data: collabLimitsPayload,
        },
      });
    }

    const customLimitsPayload = [];
    if (customFoldersWithLimits.length && customLimitsPatchUrl) {
      customFoldersWithLimits.forEach((folder) => {
        const hasLimitObj: UpdateLimitData = {
          limit_id: folder.limitId,
          value: folder.currentLimit,
          resource_type: folder.resourceType,
        };
        customLimitsPayload.push(hasLimitObj);
      });

      customLimitsPatchFetch.fetch({
        url: customLimitsPatchUrl,
        method: 'PATCH',
        body: {
          data: customLimitsPayload,
        },
      });
    }

    if (adminFoldersWithoutLimits.length) {
      adminFoldersWithoutLimits.forEach((folder) => {
        const hasNoLimitObj: CreateLimitData = {
          resource_id: folder.resourceId,
          value: folder.currentLimit,
          resourceType: folder.resourceType,
        };

        postLimits(hasNoLimitObj, 'admin', orgKey, setLimitsSuccess);
      });
    }

    if (collabFoldersWithoutLimits.length) {
      collabFoldersWithoutLimits.forEach((folder) => {
        const hasNoLimitObj: CreateLimitData = {
          resource_id: folder.resourceId,
          value: folder.currentLimit,
          resourceType: folder.resourceType,
        };

        postLimits(hasNoLimitObj, 'collaborator', orgKey, setLimitsSuccess);
      });
    }

    if (customFoldersWithoutLimits.length && customPermissionKey) {
      customFoldersWithoutLimits.forEach((folder) => {
        const hasNoLimitObj: CreateLimitData = {
          resource_id: folder.resourceId,
          value: folder.currentLimit,
          resourceType: folder.resourceType,
        };

        postLimits(hasNoLimitObj, `collaborator/${customPermissionKey}`, orgKey, setLimitsSuccess);
      });
    }
  };

  const loading =
    brandfoldersFetch.loading ||
    brandguidesFetch.loading ||
    organizationFetch.loading ||
    portalsFetch.loading ||
    bfAdminLimitsFetch.loading ||
    brandguideAdminLimitsFetch.loading ||
    orgAdminLimitsFetch.loading ||
    portalsAdminLimitsFetch.loading ||
    bfCollabLimitsFetch.loading ||
    brandguideCollabLimitsFetch.loading ||
    orgCollabLimitsFetch.loading ||
    portalsCollabLimitsFetch.loading ||
    planLimitFetch.loading ||
    customRoleLoading ||
    (customPermissionKey && (
      bfCustomLimitsFetch.loading ||
      brandguideCustomLimitsFetch.loading ||
      orgCustomLimitsFetch.loading ||
      portalsCustomLimitsFetch.loading
    ));

  if (loading) {
    return (
      <div
        className="limits-modal-loading-container"
        data-testid="limits-modal-loading-container"
      >
        <BFLoader />
      </div>
    );
  }

  // Helper function to check if a role has an error using tracked error states
  const hasRoleError = (resourceId: string, limitType: LimitTypes): boolean => {
    const key = `${resourceId}-${limitType}`;
    return errorStates.get(key) || false;
  };

  const ErrorPlaceholder: FunctionComponent<{ hasError: boolean }> = ({ hasError }) => (
    hasError ? (
      <ErrorPlaceholderDiv>
        &nbsp;
      </ErrorPlaceholderDiv>
    ) : null
  );

  // Create the table rows - one row per resource with hierarchical content within columns
  const rows: StandardTableRow[] = resourceGroups.map((group) => {
    // Find each role's data
    const adminData = group.roles.find((role) => role.role === 'Admin');
    const customData = group.roles.find((role) => role.role === 'Custom');
    const collaboratorData = group.roles.find((role) => role.role === 'Collaborator');

    // Check which roles have errors for alignment
    const adminHasError = hasRoleError(group.resourceId, 'admin');
    const customHasError = hasRoleError(group.resourceId, 'custom');
    const collaboratorHasError = hasRoleError(group.resourceId, 'collaborator');

    return {
      brandName: (
        <div style={{ paddingTop: '8px' }}>
          <span className="limits-standard-table__folder-name">
            {group.resourceName}
          </span>
        </div>
      ),
      resourceType: (
        <div style={{ paddingTop: '8px' }}>
          <ResourceLabel resourceType={group.resourceType} />
        </div>
      ),
      role: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '22px', paddingTop: '8px', paddingBottom: '8px' }}>
          <div>
            <BadgePill
              backgroundColor={BrandfolderHexColors.Admin}
              fontSize={12}
              radius={30}
            >
              Admin
            </BadgePill>
            <ErrorPlaceholder hasError={adminHasError} />
          </div>
          {customData && (
            <div>
              <div title={customData.customRoleName || 'Custom'}>
                <TruncatedBadgePill
                  backgroundColor={BrandfolderHexColors.Approver}
                  fontSize={12}
                  radius={30}
                >
                  <span className="truncate-text">
                    {customData.customRoleName || 'Custom'}
                  </span>
                </TruncatedBadgePill>
              </div>
              <ErrorPlaceholder hasError={customHasError} />
            </div>
          )}
          <div>
            <BadgePill
              backgroundColor={BrandfolderHexColors.Success}
              fontSize={12}
              radius={30}
            >
              Collaborator
            </BadgePill>
            <ErrorPlaceholder hasError={collaboratorHasError} />
          </div>
        </div>
      ),
      usage: (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '22px',
          alignItems: 'center',
          paddingTop: '8px',
          paddingBottom: '8px'
        }}>
          <div>
            <div style={{ padding: '4px', fontSize: '14px' }}>{adminData?.currentUsage || 0}</div>
            <ErrorPlaceholder hasError={adminHasError} />
          </div>
          {customData && (
            <div>
              <div style={{ padding: '4px', fontSize: '14px' }}>{customData.currentUsage || 0}</div>
              <ErrorPlaceholder hasError={customHasError} />
            </div>
          )}
          <div>
            <div style={{ padding: '4px', fontSize: '14px' }}>{collaboratorData?.currentUsage || 0}</div>
            <ErrorPlaceholder hasError={collaboratorHasError} />
          </div>
        </div>
      ),
      limit: (
        <LimitColumnContainer>
          <LimitsTableRowInput
            currentLimit={adminData?.currentLimit || 0}
            currentUsage={adminData?.currentUsage || 0}
            handleChange={(value) => updateFolderCurrentLimit(value, group.resourceId, 'Admin')}
            limitType="admin"
            resourceId={group.resourceId}
            onErrorChange={handleErrorChange}
          />
          {customData && (
            <LimitsTableRowInput
              currentLimit={customData.currentLimit || 0}
              currentUsage={customData.currentUsage || 0}
              handleChange={(value) => updateFolderCurrentLimit(value, group.resourceId, 'Custom')}
              limitType="custom"
              resourceId={group.resourceId}
              onErrorChange={handleErrorChange}
            />
          )}
          <LimitsTableRowInput
            currentLimit={collaboratorData?.currentLimit || 0}
            currentUsage={collaboratorData?.currentUsage || 0}
            handleChange={(value) => updateFolderCurrentLimit(value, group.resourceId, 'Collaborator')}
            limitType="collaborator"
            resourceId={group.resourceId}
            onErrorChange={handleErrorChange}
          />
        </LimitColumnContainer>
      ),
    };
  });

  const columns: StandardTableColumn[] = [
    {
      children: t`Resource`,
      rowKey: 'brandName',
      width: '32%',
    },
    {
      children: t`Type`,
      rowKey: 'resourceType',
      width: '18%',
    },
    {
      children: t`Role`,
      rowKey: 'role',
      tdClassName: 'limits-table-column__role',
      width: '15%',
    },
    {
      children: t`Usage`,
      rowKey: 'usage',
      tdClassName: 'limits-table-column__usage',
      width: '15%',
    },
    {
      children: t`Limit`,
      rowKey: 'limit',
      tdClassName: 'limits-table-column__limit combined-role',
      width: '20%',
    },
  ];

  // Usage total footer cell
  const currentUsageFooterCell = (
    <div className="current-usage-total">
      <span>
        {sumFoldersUsage}
      </span>
    </div>
  );

  // Limits total footer cell
  const limitsTotalFooterCell = (
    <div className='limits-total-container' data-testid="limits-total">
      <div className="limits-current-limit-count">
        <span data-testid="current-limit-total" id="current-limit-total">
          {sumFoldersLimit.toLocaleString('en-US')}
        </span>
        <span data-testid="max-plan-limit" id='current-limit-available'>
          {maxPlanLimit.toLocaleString('en-US')}
        </span>
      </div>
      <AllocationAlert
        limit={sumFoldersLimit}
        limitType="admin"
        location="footer"
        maxPlanLimit={maxPlanLimit}
      />
    </div>
  );

  // Footer row
  const footerTableRow = {
    brandName: '',
    resourceType: '',
    role: '',
    usage: currentUsageFooterCell,
    limit: limitsTotalFooterCell,
  };

  const caption = t`Set role limits for each resource`;

  return (
    <>
      {!!folders?.length && (
        <AllocationAlert
          limit={sumFoldersLimit}
          limitType="admin"
          location="header"
          maxPlanLimit={maxPlanLimit}
        />
      )}
      <div
        className="limits-standard-table combined-role-limits-table"
        data-testid="combined-role-limits-table"
      >
        <StandardTable
          breakpoints={[Breakpoints.Sm]}
          caption={caption}
          columns={columns}
          empty={!rows.length}
          emptyContent={t`No results`}
          errorContent={t`An error occurred`}
          footer={false}
          hover
          id="bf-combined-role-limits-table"
          rows={[...rows, footerTableRow]}
          scrollX
        />
        <SubmitLimitsButton
          setOpen={setOpen}
          submitDisabled={submitLimitsDisabled}
          submitUpdatedLimits={submitLimitsData}
        />
      </div>
    </>
  );
};
